import { makeAutoObservable, runInAction } from "mobx";
import { authService } from "../services";
import {
  setAccessToken as setApiClientToken,
  registerRefreshCallbacks,
  registerDashboardVariantMismatchCallback,
  signalLogout,
} from "@/lib/api/client";
import { cleanupAppStateAfterLogout } from "../utils/logout-cleanup";

const STORAGE = {
  ACCESS_TOKEN: "accessToken",
  USER: "user",
  REFRESH_TOKEN_EXPIRES_AT: "refreshTokenExpiresAt",
  LOGOUT_SIGNAL: "app_logout_signal",
};

const SESSION_WARNING_BUFFER = 2 * 60 * 1000; // 2 min before expiry

class AuthSessionStore {
  user = null;
  accessToken = null;
  refreshTokenExpiresAt = null;

  bootstrapping = true;
  bootstrapped = false;
  bootstrapPromise = null;
  isLoggingOut = false;

  showSessionWarning = false;
  showSessionExpired = false;
  sessionExpiredMessage = "";

  // non-observable
  _warningTimer = null;
  _expiryTimer = null;
  _dashboardVariantSyncPromise = null;

  constructor() {
    makeAutoObservable(this, {
      bootstrapPromise: false,
      _warningTimer: false,
      _expiryTimer: false,
      _dashboardVariantSyncPromise: false,
    });

    registerRefreshCallbacks({
      onSuccess: (data) => this._onRefreshSuccess(data),
      onFailed: () => this._onRefreshFailed(),
    });
    registerDashboardVariantMismatchCallback(
      this._handleDashboardVariantMismatch,
    );

    if (typeof window !== "undefined") {
      window.addEventListener("storage", this._onStorageEvent);
    }
  }

  // ─── Computed ───────────────────────────────────────────────────────────────
  get isJJSEA() {
    return ["6936847be9d900421638fe6b", "69804c6994f6750444d63d23"].includes(
      this.user?.companyId,
    );
  }

  get isWingletFW() {
    return ["6a150f43916c59ae3bc9b4b6"].includes(this.user?.companyId);
  }

  get isAuthenticated() {
    return Boolean(this.accessToken && this.user);
  }

  get userRole() {
    return this.user?.role;
  }

  get userGroup() {
    return this.user?.group;
  }

  get isSuperAdmin() {
    return ["super_admin", "reseller", "solution_provider"].includes(
      this.user?.role,
    );
  }

  get isDelegationOnlyUser() {
    return Boolean(this.user?.delegationOnlyAccess);
  }

  // ─── Auth success (called by authFormsStore after login / register) ────────

  applyAuthSuccess = async ({ user, accessToken, refreshTokenExpiresAt }) => {
    if (!user || !accessToken) return false;

    // Clear stale state from any previous session to prevent cross-user data leakage
    await cleanupAppStateAfterLogout();

    this.isLoggingOut = false;
    this.showSessionExpired = false;
    this.sessionExpiredMessage = "";

    this._persist({ user, accessToken, refreshTokenExpiresAt });
    this._scheduleTimers();

    try {
      // Step 2: fetch full user info from "users/me"
      const fullUserData = await authService.getMe({ throwOnError: false });
      if (fullUserData?.user) {
        runInAction(() => {
          this.user = fullUserData.user;
          localStorage.setItem(STORAGE.USER, JSON.stringify(fullUserData.user));
        });
      }
    } catch (error) {
      console.error("Failed to refresh user data after login:", error);
    }

    return true;
  };

  // ─── Bootstrap ──────────────────────────────────────────────────────────────

  bootstrap = () => {
    if (this.bootstrapPromise) return this.bootstrapPromise;

    this.bootstrapping = true;
    this.bootstrapPromise = (async () => {
      try {
        const storedUser = localStorage.getItem(STORAGE.USER);
        const storedRTE = localStorage.getItem(
          STORAGE.REFRESH_TOKEN_EXPIRES_AT,
        );
        const storedAccessToken = localStorage.getItem(STORAGE.ACCESS_TOKEN);

        if (!storedUser || !storedRTE) {
          this.clearAuth();
          return;
        }

        // Refresh token already expired on disk
        if (Date.now() >= Number(storedRTE)) {
          this.clearAuth();
          return;
        }

        let user;
        try {
          user = JSON.parse(storedUser);
        } catch {
          this.clearAuth();
          return;
        }

        // Try to refresh. Distinguish real auth failure (401/403) from
        // transient failure (api restarting during deploy, network blip, 5xx).
        // On transient failure, keep the stored access token — the 401
        // interceptor will refresh on the next real request once api is back.
        let refreshed = null;
        try {
          refreshed = await authService.refresh({ throwOnError: true });
        } catch (err) {
          const code = err?.code ?? err?.status;
          const isAuthFailure = code === 401 || code === 403;
          if (isAuthFailure) {
            this.clearAuth();
            return;
          }
          // Transient — fall through using stored access token.
        }

        const accessToken = refreshed?.accessToken || storedAccessToken;
        if (!accessToken) {
          this.clearAuth();
          return;
        }

        runInAction(() => {
          this._persist({
            user,
            accessToken,
            refreshTokenExpiresAt:
              refreshed?.refreshTokenExpiresAt || Number(storedRTE),
          });
          this._scheduleTimers();
        });

        await this.refreshMe();
      } catch {
        this.clearAuth();
      } finally {
        runInAction(() => {
          this.bootstrapping = false;
          this.bootstrapped = true;
        });
      }
    })();

    return this.bootstrapPromise;
  };

  // ─── Logout ─────────────────────────────────────────────────────────────────

  logout = async () => {
    this.isLoggingOut = true;
    this._clearTimers();
    signalLogout();
    try {
      await authService.logout();
    } finally {
      runInAction(() => {
        this.clearAuth();
      });
      await cleanupAppStateAfterLogout();
      this._redirectToLoginHard();
    }
  };

  clearAuth = () => {
    this.user = null;
    this.accessToken = null;
    this.refreshTokenExpiresAt = null;
    this.showSessionWarning = false;
    this.showSessionExpired = false;
    this.sessionExpiredMessage = "";
    this._clearTimers();
    localStorage.removeItem(STORAGE.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE.USER);
    localStorage.removeItem(STORAGE.REFRESH_TOKEN_EXPIRES_AT);
    setApiClientToken(null);
  };

  // ─── Session expired dialog ────────────────────────────────────────────────

  confirmSessionExpired = () => {
    this.clearAuth();
    this.showSessionExpired = false;
    this.sessionExpiredMessage = "";
    void cleanupAppStateAfterLogout().finally(() => {
      this._redirectToLoginHard();
    });
  };

  // ─── Session warning dialog ────────────────────────────────────────────────

  dismissSessionWarning = () => {
    this.showSessionWarning = false;
  };

  // ─── Refresh user profile (public — called by company.store, etc.) ────────

  refreshMe = async () => {
    try {
      const data = await authService.getMe({ throwOnError: false });
      if (data?.user) {
        runInAction(() => {
          this.user = data.user;
          localStorage.setItem(STORAGE.USER, JSON.stringify(data.user));
        });
      }
      return data;
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      return null;
    }
  };

  _handleDashboardVariantMismatch = (requiredDashboardVariant) => {
    if (this._dashboardVariantSyncPromise) {
      return this._dashboardVariantSyncPromise;
    }

    this._dashboardVariantSyncPromise = (async () => {
      const data = await this.refreshMe();
      const dashboardVariant =
        data?.user?.dashboardVariant || requiredDashboardVariant;
      const destination =
        dashboardVariant === "benchmark"
          ? "/benchmark-dashboard"
          : "/analytics/dashboard";

      if (
        typeof window !== "undefined" &&
        window.location.pathname !== destination
      ) {
        window.location.replace(destination);
      }
    })().finally(() => {
      this._dashboardVariantSyncPromise = null;
    });

    return this._dashboardVariantSyncPromise;
  };

  // ─── Callbacks from client.js 401 interceptor ──────────────────────────────

  _onRefreshSuccess = ({ accessToken, refreshTokenExpiresAt }) => {
    runInAction(() => {
      this._persist({ accessToken, refreshTokenExpiresAt });
      this._scheduleTimers();
    });
  };

  _onRefreshFailed = () => {
    runInAction(() => {
      if (this.showSessionExpired || this.isLoggingOut) return;
      this.isLoggingOut = true;
      this._clearTimers();
      this.sessionExpiredMessage = "Session expired. Please login again.";
      this.showSessionExpired = true;
    });
  };

  // ─── Cross-tab sync ────────────────────────────────────────────────────────

  _onStorageEvent = (e) => {
    // Another tab logged out
    if (e.key === STORAGE.LOGOUT_SIGNAL && e.newValue) {
      runInAction(() => {
        this.clearAuth();
      });
      void cleanupAppStateAfterLogout().finally(() => {
        this._redirectToLoginHard();
      });
      return;
    }

    // Another tab refreshed the access token — pick it up
    if (e.key === STORAGE.ACCESS_TOKEN) {
      if (e.newValue) {
        runInAction(() => {
          this.accessToken = e.newValue;
          setApiClientToken(e.newValue);
        });
      } else {
        runInAction(() => {
          this.clearAuth();
        });
        cleanupAppStateAfterLogout();
      }
    }
  };

  // ─── Persist state to memory + localStorage ────────────────────────────────

  _persist = ({ user, accessToken, refreshTokenExpiresAt }) => {
    if (user !== undefined) {
      this.user = user || null;
      if (user) {
        localStorage.setItem(STORAGE.USER, JSON.stringify(user));
      } else {
        localStorage.removeItem(STORAGE.USER);
      }
    }

    if (accessToken !== undefined) {
      this.accessToken = accessToken || null;
      if (accessToken) {
        localStorage.setItem(STORAGE.ACCESS_TOKEN, accessToken);
      } else {
        localStorage.removeItem(STORAGE.ACCESS_TOKEN);
      }
      setApiClientToken(accessToken);
    }

    if (refreshTokenExpiresAt !== undefined) {
      this.refreshTokenExpiresAt = refreshTokenExpiresAt || null;
      if (refreshTokenExpiresAt) {
        localStorage.setItem(
          STORAGE.REFRESH_TOKEN_EXPIRES_AT,
          String(refreshTokenExpiresAt),
        );
      } else {
        localStorage.removeItem(STORAGE.REFRESH_TOKEN_EXPIRES_AT);
      }
    }
  };

  // ─── Session timers (based on refreshTokenExpiresAt) ───────────────────────

  _scheduleTimers = () => {
    this._clearTimers();
    if (!this.refreshTokenExpiresAt) return;

    const timeUntilExpiry = this.refreshTokenExpiresAt - Date.now();

    if (timeUntilExpiry <= 0) {
      // Already expired
      this.isLoggingOut = true;
      this.showSessionWarning = false;
      this.sessionExpiredMessage = "Session expired. Please login again.";
      this.showSessionExpired = true;
      return;
    }

    // Warning: 2 min before expiry
    const warnIn = timeUntilExpiry - SESSION_WARNING_BUFFER;
    if (warnIn > 0) {
      this._warningTimer = setTimeout(() => {
        runInAction(() => {
          this.showSessionWarning = true;
        });
      }, warnIn);
    } else if (!this.showSessionWarning) {
      // Less than 2 min left — show warning immediately
      this.showSessionWarning = true;
    }

    // Expiry: fires at refreshTokenExpiresAt
    this._expiryTimer = setTimeout(() => {
      runInAction(() => {
        if (this.showSessionExpired || this.isLoggingOut) return;
        this.isLoggingOut = true;
        this._clearTimers();
        this.showSessionWarning = false;
        this.sessionExpiredMessage = "Session expired. Please login again.";
        this.showSessionExpired = true;
      });
    }, timeUntilExpiry);
  };

  _clearTimers = () => {
    if (this._warningTimer) {
      clearTimeout(this._warningTimer);
      this._warningTimer = null;
    }
    if (this._expiryTimer) {
      clearTimeout(this._expiryTimer);
      this._expiryTimer = null;
    }
  };

  _redirectToLoginHard = () => {
    if (typeof window === "undefined") return;
    window.location.replace("/login");
  };
}

export const authSessionStore = new AuthSessionStore();
